// Shared Intelligence Card shell -- the UX audit's proposed common visual
// object for the five Growth intelligence pages (Signals, Diagnoses,
// Opportunities, Recommendations, Action Plans). See
// docs/product/intelligence-console-ux-audit.md, Section 4.
//
// This is a pure layout/typography wrapper: every value it renders is
// passed in by the caller from data the page already fetched from a real
// backend route. It invents nothing, infers no tone, and adds no new data
// source of its own -- badge tone, confidence formatting, and evidence
// content all continue to come from the existing Badges.jsx /
// ConfidenceIndicator / EvidenceDrawer components, unchanged.
//
// First adopted by RecommendationsPage.js only (the audit's P0 slice).
// Signals/Diagnoses/Opportunities/Action Plans are NOT migrated yet --
// this component is written generically enough for that future work, but
// introducing it here does not change those four pages.

import { SurfaceCard } from '@/components/brand/SurfaceCard';

// Day 2.5 -- "Martian Soft Intelligence" visual system. Now built on the
// shared SurfaceCard primitive (tone="flat" -- this card repeats many
// times per page, so it stays at the restrained default weight rather
// than the "raised" tone reserved for one focal element per view).
// Everything below the outer element is unchanged: same props, same
// data flow, same Badges.jsx/ConfidenceIndicator/EvidenceDrawer callers.
export function IntelligenceCard({
  stage,
  title,
  badges,
  statementLabel,
  statement,
  metrics = [],
  lineage,
  decision,
  testId = 'intelligence-card',
  className,
}) {
  return (
    <SurfaceCard
      tone="flat"
      data-testid={testId}
      className={['p-4 sm:p-5', className].filter(Boolean).join(' ')}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          {stage && (
            <span
              data-testid="intelligence-card-stage"
              className="inline-block text-[9px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] border border-[var(--border)] rounded px-1.5 py-0.5 mb-1.5"
            >
              {stage}
            </span>
          )}
          <p data-testid="intelligence-card-title" className="text-[14px] font-semibold text-[var(--text-primary)] leading-snug">
            {title}
          </p>
        </div>
        {badges && <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">{badges}</div>}
      </div>

      {statement && (
        <div className="mb-3" data-testid="intelligence-card-statement">
          {statementLabel && (
            <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-0.5">{statementLabel}</p>
          )}
          <p className="text-[12.5px] text-[var(--text-secondary)] leading-relaxed">{statement}</p>
        </div>
      )}

      {metrics.length > 0 && (
        <div className="flex items-center gap-4 flex-wrap text-[10.5px] mb-3 pb-3 border-b border-[var(--border)]" data-testid="intelligence-card-metrics">
          {metrics.map((m, i) => (
            <div key={i} className="flex items-center gap-1.5" data-testid={m.testId || `intelligence-card-metric-${i}`}>
              <span className="text-[var(--text-secondary)]">{m.label}:</span>
              {typeof m.value === 'string' || typeof m.value === 'number' ? (
                <span className="text-[var(--text-primary)] font-mono">{m.value}</span>
              ) : (
                m.value
              )}
            </div>
          ))}
        </div>
      )}

      {lineage}

      {decision && <div className="mt-3" data-testid="intelligence-card-decision">{decision}</div>}
    </SurfaceCard>
  );
}
